import { type BillingInfo } from "lens-platform-sdk";

export const formatCreditCardType = (type: string | null | undefined): string | undefined => {
  if (!type) {
    return undefined;
  }

  switch (type.toLowerCase()) {
    case "visa":
      return "Visa";
    case "mastercard":
      return "MasterCard";
    case "amex":
      return "American Express";
    case "discover":
      return "Discover";
    case "jcb":
      return "JCB";
    case "dinersclub":
      return "Diners Club";
    default:
      return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
};

export const defaultObstructedCharacter = "●";
export type CreditCardFormatType = "long" | "short" | "text";

const visaDigitsWithSpaces = (
  firstSix?: string | null,
  lastFour?: string | null,
  obstructedCharacter = defaultObstructedCharacter,
) => {
  let digits = obstructedCharacter.repeat(16);

  if (lastFour) {
    digits = lastFour.padStart(16, obstructedCharacter);
  } else if (firstSix) {
    digits = firstSix.padEnd(16, obstructedCharacter);
  }

  return digits.replace(/(.{4})/g, "$1 ").trim();
};

const amexDigitsWithSpaces = (
  firstSix?: string | null,
  lastFour?: string | null,
  obstructedCharacter = defaultObstructedCharacter,
) => {
  let digits = obstructedCharacter.repeat(15);

  if (lastFour) {
    digits = lastFour.padStart(15, obstructedCharacter);
  } else if (firstSix) {
    digits = firstSix.padEnd(15, obstructedCharacter);
  }

  // Format as: XXXX XXXXXX XXXXXX
  return `${digits.slice(0, 4)} ${digits.slice(4, 10)} ${digits.slice(10)}`;
};

const formatValidityLong = ({ expYear, expMonth }: { expYear: Number | null; expMonth: Number | null }) =>
  expYear && expMonth
    ? new Date(
        // We need to set type as number because it was set as Number in the SDK
        expYear as number,
        (expMonth as number) - 1,
      ).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: undefined,
      })
    : "";

export const formatCreditCardNumber = (
  {
    cardType,
    firstSix,
    lastFour,
    expMonth,
    expYear,
  }: Pick<NonNullable<BillingInfo["paymentMethod"]>, "cardType" | "firstSix" | "lastFour" | "expMonth" | "expYear">,
  formatter: CreditCardFormatType = "long",
  obstructedCharacter = defaultObstructedCharacter,
): string => {
  switch (formatter) {
    case "short":
      if (lastFour) {
        return `${obstructedCharacter.repeat(3)}${lastFour}`;
      } else if (firstSix) {
        return `${firstSix}${obstructedCharacter.repeat(3)}`;
      }

      return `${obstructedCharacter.repeat(7)}`;
    case "text":
      let creditCardNumber = "";

      if (lastFour) {
        creditCardNumber = `ending with ${lastFour}`;
      } else if (firstSix) {
        creditCardNumber = `starting with ${firstSix}`;
      }

      return `${formatCreditCardType(cardType)}${creditCardNumber ? ` ${creditCardNumber}` : ""}${
        expMonth && expYear
          ? `, expiring on ${formatValidityLong({
              expMonth,
              expYear,
            })}`
          : ""
      }.`;
    default:
      const type = formatCreditCardType(cardType);

      return type === "American Express"
        ? amexDigitsWithSpaces(firstSix, lastFour, obstructedCharacter)
        : visaDigitsWithSpaces(firstSix, lastFour, obstructedCharacter);
  }
};
